// @ts-nocheck
// @ts-ignore
import { Component } from '@theme/component';
import { VariantUpdateEvent } from '@theme/events';
import { ThemeEvents } from '@theme/events';

export class ProductServiceAvailability extends Component {
  #currentVariantId = null;
  #currentPincode = null;
  #hasCheckedOnce = false;
  #boundHandleVariantUpdate = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #initialize() {
    if (!this.refs.button || !this.refs.input) {
      setTimeout(() => this.#initialize(), 10);
      return;
    }
    this.#setupEventListeners();
    this.#getCurrentVariantId();
  }

  #getCurrentVariantId() {
    const variantInput = document.querySelector('input[name="id"][type="hidden"]');
    if (variantInput) {
      this.#currentVariantId = variantInput.value;
      return;
    }
    
    const productForm = document.querySelector('product-form-component');
    if (productForm) {
      const variantIdInput = productForm.querySelector('input[name="id"]');
      if (variantIdInput) {
        this.#currentVariantId = variantIdInput.value;
        return;
      }
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get('variant');
    if (variantParam) {
      this.#currentVariantId = variantParam;
    }
  }

  #setVariantId(variantId) {
    this.#currentVariantId = variantId.toString();
  }

  #setupEventListeners() {
    if (this.refs.button) {
      this.refs.button.addEventListener('click', this.#handleCheckDelivery.bind(this));
    }

    if (this.refs.input) {
      this.refs.input.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          this.#handleCheckDelivery();
        }
      });

      // Restrict input to numeric characters and max 6 digits
      this.refs.input.addEventListener('input', (e) => {
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 6) {
          value = value.slice(0, 6);
        }
        e.target.value = value;
      });
    }

    this.#boundHandleVariantUpdate = this.#handleVariantUpdate.bind(this);
    document.addEventListener(ThemeEvents.variantUpdate, this.#boundHandleVariantUpdate);
  }

  #handleVariantUpdate(event) {
    if (!(event instanceof VariantUpdateEvent)) return;
    
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    
    const variantIdStr = variantId.toString();
    if (variantIdStr !== this.#currentVariantId?.toString()) {
      this.#setVariantId(variantIdStr);
      
      if (this.#hasCheckedOnce && this.#currentPincode) {
        this.#checkDeliveryTime(this.#currentPincode, variantIdStr);
      }
    }
  }

  #handleCheckDelivery() {
    const pincode = this.refs.input?.value?.trim();
    const pinRegex = /^[1-9][0-9]{5}$/;

    this.#hideDeliveryMessage();

    if (!pinRegex.test(pincode)) {
      this.#displayErrorMessage('Please enter a valid 6-digit PIN code.');
      return;
    }

    this.#getCurrentVariantId();
    
    if (!this.#currentVariantId) {
      console.error('Please select a variant first');
      return;
    }

    this.#currentPincode = pincode;
    this.#hasCheckedOnce = true;
    this.#checkDeliveryTime(pincode, this.#currentVariantId);
  }

  async #checkDeliveryTime(pincode, variantId) {
    if (!variantId) {
      this.#getCurrentVariantId();
      variantId = this.#currentVariantId;
      if (!variantId) return;
    }

    const button = this.refs.button;

    this.#hideDeliveryMessage();

    if (button) {
      button.classList.add('checking');
    }

    try {
      const payload = {
        dropPincode: parseInt(pincode, 10),
        quantity: 1,
        variantsData: [
          {
            variantId: variantId.toString()
          }
        ]
      };

      const response = await fetch('https://www.speedo.in/apps/somni/api/calculateEDDForProduct', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        const deliveryData = Array.isArray(data.data) ? data.data[0] : data.data;
        if (deliveryData?.pincode_serviceable && deliveryData?.expected_delivery_date) {
          this.#displayDeliveryMessage(deliveryData);
        } else {
          this.#displayErrorMessage(deliveryData?.message || 'Delivery not available for this PIN code.');
        }
      } else {
        this.#displayErrorMessage('Unable to calculate delivery time. Please try again.');
      }
    } catch (error) {
      this.#displayErrorMessage('Error checking delivery time. Please try again.');
    } finally {
      if (button) {
        button.classList.remove('checking');
      }
    }
  }

  #hideDeliveryMessage() {
    const deliveryMessage = this.refs.deliveryMessage;
    if (!deliveryMessage) return;

    deliveryMessage.classList.remove('is-visible', 'is-success', 'is-error');
  }

  #displayDeliveryMessage(deliveryData) {
    const deliveryMessage = this.refs.deliveryMessage;
    const delDate = this.querySelector('[data-delivery-date]');
    if (!deliveryMessage || !delDate) return;

    const dateStr = this.#formatDeliveryDate(deliveryData.expected_delivery_date);
    delDate.textContent = dateStr;
    deliveryMessage.classList.remove('is-error');
    deliveryMessage.classList.add('is-visible', 'is-success');
  }

  #displayErrorMessage(message) {
    const deliveryMessage = this.refs.deliveryMessage;
    const errorEl = this.querySelector('[data-delivery-error]');
    if (!deliveryMessage || !errorEl) return;

    errorEl.textContent = message;
    deliveryMessage.classList.remove('is-success');
    deliveryMessage.classList.add('is-visible', 'is-error');
  }

  #formatDeliveryDate(dateString) {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'long' });
    const suffix = this.#getDaySuffix(day);
    return `${day}${suffix} ${month}`;
  }

  #getDaySuffix(day) {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  #cleanup() {
    if (this.#boundHandleVariantUpdate) {
      document.removeEventListener(ThemeEvents.variantUpdate, this.#boundHandleVariantUpdate);
    }
  }
}

if (!customElements.get('product-service-availability')) {
  customElements.define('product-service-availability', ProductServiceAvailability);
}


